<template>
  <div class="pb-page fe">
    <router-link to="/forms" class="pb-button link pb-back fe-back"><i class="mdi mdi-arrow-left" aria-hidden="true"></i> Collection forms</router-link>
    <SkeletonList v-if="loading" :rows="3" label="Loading the form…" />
    <div v-else-if="loadError" class="pb-alert is-error" role="alert"><span>{{ loadError }}</span></div>

    <template v-else-if="form">
      <PageHeader :title="name || 'Collection page'" :subtitle="brandName + ' · ' + statusText" wide-only>
        <span v-if="dirty" class="pb-muted">Unsaved changes</span>
        <button type="button" class="pb-button" :disabled="!dirty || saving" @click="save">{{ saving ? 'Saving…' : 'Save changes' }}</button>
      </PageHeader>
      <div v-if="error" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i><span>{{ error }}</span></div>

      <div class="fe-tabs pb-segmented" role="tablist" aria-label="Form sections">
        <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :aria-selected="section === tab.key ? 'true' : 'false'" @click="section = tab.key">{{ tab.label }}</button>
      </div>

      <div v-show="section !== 'share'" class="fe-grid">
        <div class="fe-edit">
          <section v-show="section === 'questions'" class="pb-card" aria-labelledby="fe-q-title">
            <h2 id="fe-q-title" class="pb-section-title display-font fe-h">Page and questions</h2>
            <div class="pb-field"><label class="pb-label" for="fe-name">Internal name</label>
              <input id="fe-name" v-model="name" class="pb-input" maxlength="120"><span class="pb-help">Only your team sees this.</span></div>
            <div class="pb-field"><label class="pb-label" for="fe-headline">Headline</label>
              <input id="fe-headline" v-model="content.headline" class="pb-input" maxlength="120"></div>
            <div class="pb-field"><label class="pb-label" for="fe-intro">Intro <span class="optional">(optional)</span></label>
              <textarea id="fe-intro" v-model="content.intro" class="pb-textarea" rows="3" maxlength="600"></textarea></div>

            <div v-if="!custom" class="fe-presets">
              <p class="pb-label">Questions <span class="optional">(pick up to {{ presetCap }})</span></p>
              <p class="pb-help fe-help-top">The {{ planLabel }} plan uses our tested questions. <button type="button" class="pb-button link" @click="upsell('custom_questions')">Write your own with {{ planName(availableOn('custom_questions')) }}</button></p>
              <label v-for="question in presets" :key="question.label" class="pb-check fe-preset">
                <input type="checkbox" :checked="hasQuestion(question)" :disabled="!hasQuestion(question) && content.questions.length >= presetCap" @change="togglePreset(question)">
                <span>{{ question.label }}<span v-if="question.quotable" class="pb-chip is-accent fe-quotable">Quoted</span></span>
              </label>
            </div>
            <div v-else class="fe-questions">
              <p class="pb-label">Questions</p>
              <p class="pb-help fe-help-top">Mark the answer you want to quote. Customers see the help text under each question.</p>
              <div v-for="(question, index) in content.questions" :key="question.key" class="fe-question">
                <div class="fe-q-head">
                  <span class="fe-q-num">{{ index + 1 }}</span>
                  <label class="pb-sr-only" :for="'fe-ql-' + question.key">Question {{ index + 1 }}</label>
                  <input :id="'fe-ql-' + question.key" v-model="question.label" class="pb-input" maxlength="200" placeholder="Ask one thing">
                  <div class="fe-q-tools">
                    <button type="button" class="pb-icon-button" :disabled="index === 0" :aria-label="'Move question ' + (index + 1) + ' up'" @click="move(index, -1)"><i class="mdi mdi-arrow-up" aria-hidden="true"></i></button>
                    <button type="button" class="pb-icon-button" :disabled="index === content.questions.length - 1" :aria-label="'Move question ' + (index + 1) + ' down'" @click="move(index, 1)"><i class="mdi mdi-arrow-down" aria-hidden="true"></i></button>
                    <button type="button" class="pb-icon-button" :disabled="content.questions.length === 1" :aria-label="'Remove question ' + (index + 1)" @click="removeQuestion(index)"><i class="mdi mdi-delete-outline" aria-hidden="true"></i></button>
                  </div>
                </div>
                <label class="pb-sr-only" :for="'fe-qh-' + question.key">Help text for question {{ index + 1 }}</label>
                <input :id="'fe-qh-' + question.key" v-model="question.help" class="pb-input fe-q-help" maxlength="200" placeholder="Help text (optional)">
                <div class="pb-row fe-q-flags">
                  <label class="pb-check"><input v-model="question.required" type="checkbox">Required</label>
                  <label class="pb-check"><input type="radio" :name="'quotable-' + form.id" :checked="question.quotable" @change="setQuotable(index)">Quote this answer</label>
                </div>
              </div>
              <button type="button" class="pb-button outline sm" :disabled="content.questions.length >= 10" @click="addQuestion"><i class="mdi mdi-plus pb-ico" aria-hidden="true"></i>Add a question</button>
            </div>
          </section>

          <section v-show="section === 'fields'" class="pb-card" aria-labelledby="fe-f-title">
            <h2 id="fe-f-title" class="pb-section-title display-font fe-h">Details we ask for</h2>
            <p class="pb-help fe-help-top">Customers choose separately which of these details may be shown with their words.</p>
            <div v-for="field in fieldList" :key="field.key" class="fe-field-row">
              <span :id="'fe-fl-' + field.key" class="fe-field-name">{{ field.label }}<span v-if="field.note" class="pb-muted">{{ field.note }}</span></span>
              <div class="pb-segmented" role="radiogroup" :aria-labelledby="'fe-fl-' + field.key">
                <button v-for="mode in modes" :key="mode.key" type="button" role="radio" :aria-checked="content.fields[field.key] === mode.key ? 'true' : 'false'"
                        :aria-pressed="content.fields[field.key] === mode.key ? 'true' : 'false'" @click="content.fields[field.key] = mode.key">{{ mode.label }}</button>
              </div>
            </div>
            <label class="pb-check fe-anon"><input v-model="content.allow_anonymous" type="checkbox">Allow anonymous feedback (the name becomes optional)</label>
            <div class="pb-field fe-thanks"><label class="pb-label" for="fe-thanks">Thank-you message</label>
              <textarea id="fe-thanks" v-model="content.thank_you" class="pb-textarea" rows="2" maxlength="400"></textarea></div>
            <label class="pb-check"><input type="checkbox" :checked="content.remove_branding" @change="toggleBranding($event)">Hide "Collected with TrustClap"
              <PlanChip v-if="!limits.remove_branding" :plan="availableOn('remove_branding')" /></label>
          </section>
        </div>

        <aside class="fe-preview" aria-label="Live preview">
          <div class="pb-row fe-preview-top">
            <span class="pb-label fe-preview-label">Live preview</span>
            <div class="pb-segmented" role="group" aria-label="Preview size">
              <button type="button" :aria-pressed="device === 'phone' ? 'true' : 'false'" @click="device = 'phone'">Phone</button>
              <button type="button" :aria-pressed="device === 'desktop' ? 'true' : 'false'" @click="device = 'desktop'">Desktop</button>
            </div>
          </div>
          <PhoneFrame v-if="device === 'phone'" :url="shortUrl" label="Your collection page on a phone">
            <CollectFormPreview :form="content" :brand="previewBrand" :attribution="!content.remove_branding" />
          </PhoneFrame>
          <div v-else class="fe-desktop">
            <div class="fe-desktop-bar" aria-hidden="true"><span></span><span></span><span></span><em>{{ shortUrl }}</em></div>
            <div class="fe-desktop-page"><CollectFormPreview :form="content" :brand="previewBrand" :attribution="!content.remove_branding" /></div>
          </div>
        </aside>
      </div>

      <section v-show="section === 'share'" class="fe-share" aria-labelledby="fe-s-title">
        <h2 id="fe-s-title" class="pb-sr-only">Share</h2>
        <div class="pb-card fe-share-main">
          <div class="pb-card-head"><h3 class="pb-section-title display-font">Your link</h3>
            <span :class="['pb-chip', form.status === 'active' ? 'approved' : 'is-warning']">{{ statusText }}</span></div>
          <SnippetBox :value="link" label="Collection page link" @copied="copied" />
          <div class="pb-actions fe-link-actions">
            <a :href="link" target="_blank" rel="noopener" class="pb-button sm outline">Open page<span class="pb-sr-only"> (opens in a new tab)</span></a>
          </div>
          <div class="fe-status">
            <span id="fe-status-label" class="pb-label">Status</span>
            <div class="pb-segmented" role="radiogroup" aria-labelledby="fe-status-label">
              <button v-for="option in statuses" :key="option.key" type="button" role="radio" :aria-checked="form.status === option.key ? 'true' : 'false'"
                      :aria-pressed="form.status === option.key ? 'true' : 'false'" @click="setStatus(option.key)">{{ option.label }}</button>
            </div>
            <p class="pb-help">A paused or archived page tells visitors it isn't accepting responses right now.</p>
          </div>
        </div>
        <div class="pb-card fe-qr">
          <h3 class="pb-section-title display-font">QR code</h3>
          <p class="pb-muted">For receipts, counters and packaging. It opens the same link.</p>
          <QrCode :value="link" :size="176" :filename="'trustclap-' + form.key" />
        </div>
        <div class="pb-card fe-messages">
          <h3 class="pb-section-title display-font">Ready-to-send messages</h3>
          <p class="pb-muted">Copy one into your email, WhatsApp or text. Replace "there" with the customer's name.</p>
          <div v-for="item in messages" :key="item.key" class="fe-message">
            <SnippetBox :value="fill(item.text)" :label="item.name" multiline prose :rows="4" @copied="copied" />
          </div>
        </div>
      </section>
    </template>
    <SaveBar :dirty="dirty" :saving="saving" @save="save" />
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import CollectFormPreview, { defaultFields } from '../components/CollectFormPreview.vue';
import PageHeader from '../components/PageHeader.vue';
import SaveBar from '../components/SaveBar.vue';
import PhoneFrame from '../components/PhoneFrame.vue';
import PlanChip from '../components/PlanChip.vue';
import QrCode from '../components/QrCode.vue';
import SkeletonList from '../components/SkeletonList.vue';
import SnippetBox from '../components/SnippetBox.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, patch, post, message, planLimit } from '../api';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';
import { collectUrl, imageUrl } from '../publicUrls';

let keySeq = 0;
const withKey = question => Object.assign({ key: 'k' + (++keySeq), help: '', required: false, quotable: false }, question);
const FIELDS = [
  { key: 'rating', label: 'Star rating' }, { key: 'name', label: 'Name' }, { key: 'title', label: 'Job title' },
  { key: 'company', label: 'Company' }, { key: 'photo', label: 'Photo' },
  { key: 'contact', label: 'Email address', note: ' · private, never shown' }
];

export default {
  name: 'FormEditorView',
  components: { CollectFormPreview, PageHeader, PhoneFrame, PlanChip, QrCode, SaveBar, SkeletonList, SnippetBox, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ form: null, name: '', content: null, saved: '', loading: true, loadError: '', error: '', saving: false,
    section: 'questions', device: 'phone', templates: [], messages: [], upgrade: null, upgradeOpen: false,
    modes: [{ key: 'off', label: 'Off' }, { key: 'optional', label: 'Optional' }, { key: 'required', label: 'Required' }],
    statuses: [{ key: 'active', label: 'Accepting' }, { key: 'paused', label: 'Paused' }, { key: 'archived', label: 'Archived' }],
    fieldList: FIELDS }),
  computed: {
    tabs() { return [{ key: 'questions', label: 'Questions' }, { key: 'fields', label: 'Details' }, { key: 'share', label: 'Share' }]; },
    custom() { return !!(this.form && this.form.limits && this.form.limits.custom_questions); },
    // How many preset questions the plan allows on a page (policy.LIMITS preset_questions).
    presetCap() {
      const limits = (this.form && this.form.limits) || this.limits;
      return typeof limits.preset_questions === 'number' ? limits.preset_questions : this.limits.preset_questions || 3;
    },
    formBrand() { return this.form ? (this.ws.brands || []).find(item => item.id === this.form.brand_id) || this.form.brand : null; },
    brandName() { return this.formBrand ? this.formBrand.name : ''; },
    previewBrand() {
      const brand = this.formBrand || {};
      return { name: brand.name, color: brand.color, logo_url: brand.logo && brand.logo.status === 'complete' ? imageUrl(brand.logo.key) : '' };
    },
    link() { return collectUrl(this.form); },
    shortUrl() { return this.link.replace(/^https?:\/\//, ''); },
    statusText() { return { active: 'Accepting responses', paused: 'Paused', archived: 'Archived' }[this.form ? this.form.status : ''] || ''; },
    snapshot() { return JSON.stringify({ name: this.name, content: this.cleanContent() }); },
    dirty() { return !!this.content && this.snapshot !== this.saved; },
    presets() {
      const own = this.templates.find(item => item.key === this.form.template_key);
      const basic = this.templates.filter(item => !item.locked && item !== own);
      const list = [];
      [own].concat(basic).filter(Boolean).forEach(item => item.questions.forEach(question => {
        if (!list.some(entry => entry.label === question.label)) list.push(question);
      }));
      return list;
    }
  },
  watch: { '$route.params.id'() { this.load(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    window.addEventListener('beforeunload', this.guard);
    await this.load();
  },
  beforeDestroy() { window.removeEventListener('beforeunload', this.guard); },
  async beforeRouteLeave(to, from, next) {
    if (this.dirty && !(await ask('Leave without saving your changes?', { action: 'Leave' }))) { next(false); return; }
    next();
  },
  methods: {
    guard(event) { if (this.dirty) { event.preventDefault(); event.returnValue = ''; } },
    take(form) {
      this.form = form;
      this.name = form.name;
      const content = JSON.parse(JSON.stringify(form.content || {}));
      content.fields = Object.assign(defaultFields(), content.fields || {});
      content.questions = (content.questions || []).map(withKey);
      content.intro = content.intro || '';
      content.thank_you = content.thank_you || '';
      this.content = content;
      this.saved = this.snapshot;
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const [form, library] = await Promise.all([get('/forms/' + this.$route.params.id), get('/question-templates')]);
        this.templates = library.templates || [];
        this.messages = library.messages || [];
        this.take(form);
        if (this.$route.query.tab === 'share') this.section = 'share';
      } catch (error) {
        this.loadError = message(error, 'This collection page no longer exists.');
      } finally { this.loading = false; }
    },
    cleanContent() {
      if (!this.content) return null;
      const content = JSON.parse(JSON.stringify(this.content));
      content.questions = content.questions.filter(q => (q.label || '').trim()).map(q => ({ id: q.id || null, label: q.label.trim(), help: (q.help || '').trim(), required: !!q.required, quotable: !!q.quotable }));
      return content;
    },
    upsell(feature) {
      this.upgrade = { feature };
      this.upgradeOpen = true;
    },
    hasQuestion(question) { return this.content.questions.some(q => q.label === question.label); },
    togglePreset(question) {
      if (this.hasQuestion(question)) {
        if (this.content.questions.length === 1) return;
        this.content.questions = this.content.questions.filter(q => q.label !== question.label);
      } else if (this.content.questions.length < this.presetCap) {
        const order = this.presets.map(q => q.label);
        this.content.questions = this.content.questions.concat([withKey({ id: question.id, label: question.label, help: question.help,
          required: !!question.quotable, quotable: !!question.quotable })]).sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
      }
      if (!this.content.questions.some(q => q.quotable)) this.content.questions[0].quotable = true;
    },
    addQuestion() { this.content.questions.push(withKey({ label: '' })); },
    removeQuestion(index) {
      const removed = this.content.questions.splice(index, 1)[0];
      if (removed && removed.quotable && this.content.questions.length) this.content.questions[0].quotable = true;
    },
    move(index, step) {
      const list = this.content.questions.slice();
      const [item] = list.splice(index, 1);
      list.splice(index + step, 0, item);
      this.content.questions = list;
    },
    setQuotable(index) { this.content.questions.forEach((q, i) => { q.quotable = i === index; }); },
    toggleBranding(event) {
      if (event.target.checked && !this.limits.remove_branding && !this.form.content.remove_branding) {
        event.target.checked = false;
        this.upsell('remove_branding');
        return;
      }
      this.content.remove_branding = event.target.checked;
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        this.take(await patch('/forms/' + this.form.id, { name: this.name.trim() || this.form.name, content: this.cleanContent() }));
        toast('Saved. Your page is up to date.');
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else this.error = message(error);
      } finally { this.saving = false; }
    },
    async setStatus(status) {
      if (status === this.form.status) return;
      try {
        const updated = await post('/forms/' + this.form.id + '/status', { status });
        this.form = Object.assign({}, this.form, { status: updated.status });
        toast(status === 'active' ? 'The page accepts responses again.' : 'Status changed.');
        this.refreshWorkspace();
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      }
    },
    fill(text) {
      return text.replace('{customer}', 'there').replace(/\{business\}/g, this.brandName).replace('{link}', this.link);
    },
    copied() { toast('Link copied.'); }
  }
};
</script>

<style scoped>
.fe-back{margin-bottom:16px}
.fe-tabs{margin-bottom:20px}
.fe-grid{display:grid;grid-template-columns:minmax(0,1.1fr) minmax(0,.9fr);gap:28px;align-items:start}
.fe-h{margin-bottom:18px}
.fe-help-top{margin:-2px 0 14px}
.fe-preset{padding:10px 12px;border:1px solid #E6DED2;border-radius:10px;margin-bottom:8px!important}
.fe-quotable{margin-left:8px}
.fe-question{padding:14px;border:1px solid #E6DED2;border-radius:12px;margin-bottom:12px;background:#FBF8F3}
.fe-q-head{display:flex;gap:10px;align-items:center}
.fe-q-num{display:inline-grid;place-items:center;width:28px;height:28px;flex:0 0 28px;border-radius:50%;background:#fff;border:1px solid #D4C9B8;font:700 13px var(--font-body);color:#4F4A45}
.fe-q-tools{display:flex;flex:0 0 auto}
.fe-q-help{margin-top:8px;font-size:14px}
.fe-q-flags{gap:18px;margin-top:10px;flex-wrap:wrap}
.fe-field-row{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid #EFE9E0;flex-wrap:wrap}
.fe-field-name{font:600 14.5px var(--font-body);color:#1F2328}
.fe-anon{margin:18px 0!important}
.fe-thanks{margin-top:8px}
.fe-preview{position:sticky;top:84px}
.fe-preview-top{justify-content:space-between;margin-bottom:12px}
.fe-preview-label{margin:0}
.fe-desktop{border:1px solid #D4C9B8;border-radius:14px;overflow:hidden;background:#fff;box-shadow:var(--pb-shadow-card)}
.fe-desktop-bar{display:flex;align-items:center;gap:6px;padding:10px 14px;background:#F3EEE6;border-bottom:1px solid #E6DED2}
.fe-desktop-bar span{width:10px;height:10px;border-radius:50%;background:#D4C9B8}
.fe-desktop-bar em{margin-left:10px;font:500 12px/1 var(--font-body);font-style:normal;color:#6B635B;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fe-desktop-page{max-height:720px;overflow:auto;padding:28px;background:#FAF7F2}
.fe-share{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,.7fr);gap:20px;align-items:start}
.fe-share-main .pb-card-head{flex-wrap:wrap}
.fe-link-actions{margin:12px 0 22px}
.fe-status .pb-help{margin-top:8px}
.fe-qr{text-align:left}
.fe-qr .pb-muted{margin:6px 0 14px}
.fe-messages{grid-column:1/-1}
.fe-messages>.pb-muted{margin:6px 0 16px}
.fe-message{margin-bottom:14px}
@media(max-width:1100px){.fe-grid{grid-template-columns:minmax(0,1fr)}.fe-preview{position:static}}
@media(max-width:800px){.fe-share{grid-template-columns:minmax(0,1fr)}}
@media(max-width:600px){.fe-q-head{flex-wrap:wrap}.fe-q-head .pb-input{flex:1 1 calc(100% - 40px)}.fe-q-tools{margin-left:auto}}
</style>
